// A2F-650: field catalogue for the FRE redesign Step 2 configurator.
//
// Two purposes:
//
//  1. BASE_FORMATTING — flat dotted-key defaults sourced from CHRONA_DEFAULT
//     (chrona-addin/src/commands/formatting.js). WYSIWYG: what you see in
//     Step 2 is what saves to the profile if the user customizes, and what
//     they inherit if they don't (D70 inherit-don't-copy still applies via
//     buildProfile's optional formatters arg).
//
//  2. FIELD_GROUPS — render metadata. Each card declares the keys it
//     reads, optional inline editors, how to display the composite value
//     when rendering static, and whether it persists to Formatters.
//
// The shape here intentionally does NOT mirror Formatters 1:1. Headings
// in Formatters use HeadingLevelMap nesting; we use dotted keys
// ("headings.sizes.1") so the pill reducer is a single Object.assign.
// formattersFromState() in lib/onboard-v2-formatting.ts does the
// flat→nested conversion at submit time.

import type { TableStylePreset } from '../types'

export type FormattingValue = string | number | boolean
export type FormattingFields = Record<string, FormattingValue>

// A2F-705: single neutral seed (was Light + Dark pre-A2F-705). The
// configurator's onboarding copy nudges the user to rename it for their
// first table type. Values are the pre-A2F-548 Light palette verbatim.
// Server (formatter_validator.default_table_style_presets) owns the
// canonical copy and seeds GET /ribbon/profile responses; the client
// only ever falls back to this when the GET hasn't completed yet
// (brand-new user typing into /configure before their first save).
// Four homes kept in lockstep:
//   - this file                                     (DEFAULT_TABLE_STYLES)
//   - src/commands/formatting.js                    (TABLE_STYLE_DEFAULTS)
//   - src/taskpane/taskpane-pro.js                  (_DEFAULT_PRESET_ID
//       — anchors seed-id quirks; values aren't duplicated, but the
//       id string MUST stay aligned with this seed's `id` field)
//   - backend/services/formatter_validator.py       (_DEFAULT_TABLE_STYLE)
// Each surface (taskpane bundle / web bundle / backend) has its own
// bundler / runtime, and a shared module would pull cross-bundle deps
// that aren't worth the savings.
export const DEFAULT_TABLE_STYLES: TableStylePreset[] = [
  {
    id: 'default',
    name: 'Default',
    headerBg: '#E7E6E6',
    headerFont: 'Calibri',
    headerFontSize: 10,
    headerTextColor: '#333333',
    headerBold: true,
    bodyFont: 'Calibri',
    bodyFontSize: 10,
    bodyTextColor: '#333333',
    defaultCellBg: '#FFFFFF',
    altRowBg: '#FFFFFF',
    borderColor: '#999999',
    borderWidth: 0.5,
  },
]

// ── Defaults sourced from CHRONA_DEFAULT (formatting.js) ────────────

// A2F-701: the legacy single-preset `tables.*` keys (backed by the
// formatters.tables JSONB slot the now-defunct ExecuteFunction
// applyTableStyle handler in commands-pro.js consumes) and the
// two-preset `tableStyle.{light,dark}.*` canary keys are both gone.
// Table styles now live in their own state slice owned by
// ConfiguratorCore (an ordered TableStylePreset[] keyed by id) and
// the editor renders via TableStylesEditor, not the dotted-key card
// reducer. Removing the legacy + canary entries here makes any
// stray BASE_FORMATTING reads clear they belong elsewhere now.
export const BASE_FORMATTING: FormattingFields = {
  // Headings — see CHRONA_DEFAULT.headings (A2F-730 realigned to Word's
  // Office defaults: Calibri Light heading theme, accent1 colors, looser
  // bold pattern). Lockstep with chrona-addin/src/commands/formatting.js.
  'headings.font': 'Calibri Light',
  'headings.sizes.1': 18,
  'headings.sizes.2': 14,
  'headings.sizes.3': 12,
  'headings.bold.1': true,
  'headings.bold.2': false,
  'headings.bold.3': false,
  // A2F-706: per-level heading color. Configurator surfaces 1-3 only
  // (matches the existing 1-3-only sizes/bold pattern); H4-H6 use the
  // schema slot but stay editor-less. Defaults match
  // CHRONA_DEFAULT.headings.colors so an unedited save still omits the
  // group per the D70 inherit-don't-copy rule.
  'headings.colors.1': '#2F5496',
  'headings.colors.2': '#2F5496',
  'headings.colors.3': '#1F3763',

  // Body — see CHRONA_DEFAULT.body
  'body.font': 'Arial',
  'body.size': 11,
  'body.lineSpacing': 1.15,
  'body.spaceAfter': 6,

  // Figure caption — see CHRONA_DEFAULT.figureCaption
  'figureCaption.font': 'Arial',
  'figureCaption.size': 9,
  'figureCaption.italic': true,
  'figureCaption.color': '#000000',

  // A2F-700-caption: Table caption — see CHRONA_DEFAULT.tableCaption.
  // Defaults match figureCaption today; the handler in formatting.js
  // distinguishes the two on alignment (Center for figure, Left for
  // table) but those slots aren't surfaced as editors in /configure.
  'tableCaption.font': 'Arial',
  'tableCaption.size': 9,
  'tableCaption.italic': true,
  'tableCaption.color': '#000000',
}

// ── Editor metadata ────────────────────────────────────────────────
//
// Each editor declares an input kind + the key it edits. FieldCard reads
// `editors` and renders inline inputs in order; if `editors` is empty
// the card renders the static `render()` output (display-only cards
// like Numbering, List bullet, etc.).
//
// Suffix lets us inline a unit ("pt", "after") between editors without
// inventing a separate "static text" editor type.

export interface ColorEditor {
  kind: 'color'
  key: string
}
export interface NumberEditor {
  kind: 'number'
  key: string
  min?: number
  max?: number
  step?: number
  suffix?: string
}
export interface FontEditor {
  kind: 'font'
  key: string
}
export interface BoolEditor {
  kind: 'bool'
  key: string
  label: string
}
export type EditorDef =
  | ColorEditor
  | NumberEditor
  | FontEditor
  | BoolEditor

// Curated font list — pairs CHRONA_DEFAULT (Arial / Aptos) with the
// most common alternatives regulatory writers reach for. Keep this
// short: a 30-option list dilutes the choice and most options would be
// missing from the user's installed set anyway.
export const FONT_OPTIONS = [
  'Arial',
  'Aptos',
  'Calibri',
  'Calibri Light',
  'Cambria',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
] as const

// FIELD_GROUPS drives the rendered grid. Each card lists the keys it
// reads (for change detection), optional inline editors, and a static
// `render()` for cards without editors. `persistable` controls whether
// formattersFromState includes the card's keys at save time.

export interface FieldCardDef {
  id: string
  label: string
  keys: string[]
  // Inline editors. Empty for display-only cards (numbering, list
  // bullet, etc.) — those render the `render()` output as static text.
  editors: EditorDef[]
  // Used by display-only cards. Editable cards may also provide this
  // for the "static fallback" view (currently unused but cheap to keep).
  render: (state: FormattingFields) => string
  persistable: boolean
}

export interface FieldGroupDef {
  id: 'headings' | 'body' | 'captions' | 'lists'
  title: string
  cards: FieldCardDef[]
}

function fmtBool(value: FormattingValue, label: string): string {
  return value ? label : ''
}

function fmtSize(value: FormattingValue): string {
  return `${value}pt`
}

export const FIELD_GROUPS: FieldGroupDef[] = [
  // A2F-701: the legacy "Tables" group (formatters.tables) was retired
  // here. Pro's manifest never exposed the applyTableStyle
  // ExecuteFunction handler that single-preset slot fed, so editing
  // those cards stored bytes nothing rendered. The Apply Table Style
  // surface now lives in TableStylesEditor, owned by ConfiguratorCore,
  // backed by formatters.tableStyles[]. The schema slot for
  // formatters.tables is left in the data model (Lumen / future
  // tooling) — only the configurator UI exposure went away.
  {
    id: 'headings',
    title: 'Headings',
    cards: [
      {
        id: 'headings.h1',
        label: 'Heading 1',
        keys: ['headings.font', 'headings.sizes.1', 'headings.bold.1', 'headings.colors.1'],
        editors: [
          { kind: 'font', key: 'headings.font' },
          { kind: 'number', key: 'headings.sizes.1', min: 8, max: 32, step: 0.5, suffix: 'pt' },
          { kind: 'bool', key: 'headings.bold.1', label: 'bold' },
          { kind: 'color', key: 'headings.colors.1' },
        ],
        render: (s) =>
          `${s['headings.font']} ${fmtSize(s['headings.sizes.1'])} ${fmtBool(s['headings.bold.1'], 'bold')} ${s['headings.colors.1']}`.trim(),
        persistable: true,
      },
      {
        id: 'headings.h2',
        label: 'Heading 2',
        keys: ['headings.font', 'headings.sizes.2', 'headings.bold.2', 'headings.colors.2'],
        editors: [
          { kind: 'font', key: 'headings.font' },
          { kind: 'number', key: 'headings.sizes.2', min: 8, max: 32, step: 0.5, suffix: 'pt' },
          { kind: 'bool', key: 'headings.bold.2', label: 'bold' },
          { kind: 'color', key: 'headings.colors.2' },
        ],
        render: (s) =>
          `${s['headings.font']} ${fmtSize(s['headings.sizes.2'])} ${fmtBool(s['headings.bold.2'], 'bold')} ${s['headings.colors.2']}`.trim(),
        persistable: true,
      },
      {
        id: 'headings.h3',
        label: 'Heading 3',
        keys: ['headings.font', 'headings.sizes.3', 'headings.bold.3', 'headings.colors.3'],
        editors: [
          { kind: 'font', key: 'headings.font' },
          { kind: 'number', key: 'headings.sizes.3', min: 8, max: 32, step: 0.5, suffix: 'pt' },
          { kind: 'bool', key: 'headings.bold.3', label: 'bold' },
          { kind: 'color', key: 'headings.colors.3' },
        ],
        render: (s) =>
          `${s['headings.font']} ${fmtSize(s['headings.sizes.3'])} ${fmtBool(s['headings.bold.3'], 'bold')} ${s['headings.colors.3']}`.trim(),
        persistable: true,
      },
      {
        id: 'headings.numbering',
        label: 'Numbering',
        keys: [],
        editors: [],
        render: () => 'eCTD (3.2.P.5)',
        persistable: false,
      },
    ],
  },
  {
    id: 'body',
    title: 'Body text',
    cards: [
      {
        id: 'body.font',
        label: 'Font',
        keys: ['body.font', 'body.size'],
        editors: [
          { kind: 'font', key: 'body.font' },
          { kind: 'number', key: 'body.size', min: 8, max: 18, step: 0.5, suffix: 'pt' },
        ],
        render: (s) => `${s['body.font']} ${fmtSize(s['body.size'])}`,
        persistable: true,
      },
      {
        id: 'body.spacing',
        label: 'Line spacing',
        keys: ['body.lineSpacing', 'body.spaceAfter'],
        editors: [
          { kind: 'number', key: 'body.lineSpacing', min: 1, max: 3, step: 0.05 },
          { kind: 'number', key: 'body.spaceAfter', min: 0, max: 24, step: 1, suffix: 'pt' },
        ],
        render: (s) => `${s['body.lineSpacing']} / ${s['body.spaceAfter']}pt after`,
        persistable: true,
      },
    ],
  },
  {
    id: 'captions',
    title: 'Captions',
    cards: [
      {
        id: 'figureCaption.style',
        label: 'Figure caption style',
        keys: [
          'figureCaption.font',
          'figureCaption.size',
          'figureCaption.italic',
          'figureCaption.color',
        ],
        editors: [
          { kind: 'font', key: 'figureCaption.font' },
          { kind: 'number', key: 'figureCaption.size', min: 6, max: 14, step: 0.5, suffix: 'pt' },
          { kind: 'bool', key: 'figureCaption.italic', label: 'italic' },
          { kind: 'color', key: 'figureCaption.color' },
        ],
        render: (s) =>
          `${s['figureCaption.font']} ${fmtSize(s['figureCaption.size'])} ${
            s['figureCaption.italic'] ? 'italic ' : ''
          }${s['figureCaption.color']}`,
        persistable: true,
      },
      // A2F-700-caption: tableCaption gets its own editor card now that
      // backend validation accepts formatters.tableCaption (A2F-691 made
      // the ribbon handler profile-driven; the validator caught up in
      // this PR). Mirror of figureCaption.style with a distinct render
      // tag so the configurator preview reads side-by-side.
      {
        id: 'tableCaption.style',
        label: 'Table caption style',
        keys: [
          'tableCaption.font',
          'tableCaption.size',
          'tableCaption.italic',
          'tableCaption.color',
        ],
        editors: [
          { kind: 'font', key: 'tableCaption.font' },
          { kind: 'number', key: 'tableCaption.size', min: 6, max: 14, step: 0.5, suffix: 'pt' },
          { kind: 'bool', key: 'tableCaption.italic', label: 'italic' },
          { kind: 'color', key: 'tableCaption.color' },
        ],
        render: (s) =>
          `${s['tableCaption.font']} ${fmtSize(s['tableCaption.size'])} ${
            s['tableCaption.italic'] ? 'italic ' : ''
          }${s['tableCaption.color']}`,
        persistable: true,
      },
      {
        id: 'figureCaption.position',
        label: 'Position',
        keys: [],
        editors: [],
        render: () => 'Below table/figure',
        persistable: false,
      },
    ],
  },
  {
    id: 'lists',
    title: 'Lists',
    cards: [
      {
        id: 'lists.bullet',
        label: 'Bullet style',
        keys: [],
        editors: [],
        render: () => 'Filled circle',
        persistable: false,
      },
      {
        id: 'lists.indent',
        label: 'Indent',
        keys: [],
        editors: [],
        render: () => '0.5” per level',
        persistable: false,
      },
    ],
  },
]
